import * as Sentry from '@sentry/react'

/**
 * Initialize Sentry error tracking + tracing.
 *
 * No-op when `VITE_SENTRY_DSN` is unset (local dev, tests). This keeps the
 * dev experience clean and avoids accidentally sending traces during CI runs.
 *
 * Safe data: tokens / passwords / refresh tokens are scrubbed from the event
 * payload via `beforeSend` and `beforeBreadcrumb` so we never leak secrets.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  Sentry.init({
    dsn,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend(event) {
      return scrubSecrets(event)
    },
    beforeBreadcrumb(crumb) {
      // Drop fetch breadcrumbs that contain auth tokens in the URL.
      if (crumb.category === 'fetch' && typeof crumb.data?.url === 'string') {
        if (/access_token=/.test(crumb.data.url)) return null
      }
      return crumb
    },
  })
}

/**
 * Forward an error to Sentry. Used by `notify.error()` and the `ErrorBoundary`.
 * No-op when Sentry hasn't been initialized.
 */
export function reportError(err: unknown, context?: Record<string, unknown>) {
  if (!err) return
  Sentry.captureException(err, context ? { extra: context } : undefined)
}

/** Re-scrub secret-shaped fields from any event before it leaves the browser. */
function scrubSecrets(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  const blockedKeys = /^(password|token|refresh.?token|authorization|access.?token)$/i
  function clean(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(clean)
    if (value && typeof value === 'object') {
      const out: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = blockedKeys.test(k) ? '[Filtered]' : clean(v)
      }
      return out
    }
    return value
  }
  if (event.extra) event.extra = clean(event.extra) as Record<string, unknown>
  if (event.request?.headers) {
    event.request.headers = clean(event.request.headers) as Record<string, string>
  }
  return event
}
